import { useEffect, useState } from 'react';
import { CreditCard, Check, Clock, ShieldAlert, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

const fmtDate = (iso) => {
  if (!iso) return '-';
  try { return new Date(iso).toLocaleDateString('da-DK', { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return iso; }
};

const fmtPrice = (n) => new Intl.NumberFormat('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n ?? 0);

function PriceBreakdown({ total, testIdPrefix }) {
  const base = total / 1.25; // total = base * 1.25 → base = total / 1.25
  const vat = total - base;
  return (
    <div className="rounded-md border border-border/60 bg-black/20 p-3 text-xs mb-6 space-y-1" data-testid={`${testIdPrefix}-breakdown`}>
      <div className="flex justify-between text-muted-foreground">
        <span>Ekskl. moms</span>
        <span data-testid={`${testIdPrefix}-base`}>{fmtPrice(base)} DKK</span>
      </div>
      <div className="flex justify-between text-muted-foreground">
        <span>Moms 25%</span>
        <span data-testid={`${testIdPrefix}-vat`}>{fmtPrice(vat)} DKK</span>
      </div>
      <div className="flex justify-between text-white font-semibold pt-1 border-t border-border/40">
        <span>Total (inkl. moms)</span>
        <span data-testid={`${testIdPrefix}-total`}>{fmtPrice(total)} DKK</span>
      </div>
    </div>
  );
}

export default function PaymentPage() {
  const { hasSub } = useAuth();
  const [subs, setSubs] = useState([]);
  const [params, setParams] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(null); // 'monthly' | 'hourly' | null

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [s, p, i] = await Promise.all([
        api.get('/subscriptions/me'),
        api.get('/parameters'),
        api.get('/invoices/me').catch(() => ({ data: [] })),
      ]);
      setSubs(s.data);
      setParams(p.data);
      setInvoices(i.data);
    } catch {
      toast.error('Kunne ikke hente betalingsdata');
    } finally { setLoading(false); }
  };

  const buy = async (plan) => {
    setBuying(plan);
    try {
      const { data } = await api.post('/payments/checkout', {
        origin_url: window.location.origin,
        plan,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Betalingsfejl');
      setBuying(null);
    }
  };

  const startTrial = null; // trial removed from UI Feb 2026

  const isNewCustomer = invoices.length === 0;
  const activeSub = subs.find((s) => new Date(s.expire_date) > new Date());

  const daysUntilExpiry = activeSub
    ? Math.ceil((new Date(activeSub.expire_date).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    : null;
  const expiringSoon = daysUntilExpiry !== null && daysUntilExpiry > 0 && daysUntilExpiry <= 7;

  // Prices from PARAMETERS
  const monthlyIntro = params?.monthly_price_first ?? 29.99;
  const monthlyRegular = params?.monthly_price_recurring ?? 49.99;
  const hourlyIntro = params?.price_paid_minutes_first_order ?? 39.00;
  const hourlyRegular = params?.price_paid_minutes_recurring_orders ?? 49.00;
  const minutesBought = params?.minutes_bought ?? 60;

  // Format minutes as Danish hours. Rules:
  //   • Less than 60 min          → "30 min."   (keep minutes)
  //   • Exactly 60 min            → "1 time"    (singular)
  //   • Clean multiple of 60      → "2 timer"   (plural)
  //   • Anything else             → "1,5 timer" (1 decimal with Danish comma)
  const formatHours = (min) => {
    const m = Number(min) || 0;
    if (m < 60) return `${m} min.`;
    if (m === 60) return '1 time';
    if (m % 60 === 0) return `${m / 60} timer`;
    const hours = m / 60;
    return `${hours.toFixed(1).replace('.', ',')} timer`;
  };
  const hoursLabel = formatHours(minutesBought);

  return (
    <div className="px-6 md:px-12 py-10 md:py-16 pb-24 md:pb-16 max-w-6xl">
      <div className="mb-10 animate-fade-up">
        <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Abonnement</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">Betaling</h1>
      </div>

      {/* Expiring-soon banner */}
      {expiringSoon && (
        <div
          className="mb-6 flex items-start gap-3 rounded-lg border border-yellow-400/40 bg-yellow-400/10 p-4 animate-fade-up"
          data-testid="payment-expiring-banner"
        >
          <AlertTriangle className="w-5 h-5 text-yellow-400 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-sm text-yellow-400">
              {daysUntilExpiry === 1 ? 'Dit abonnement udløber i morgen' : `Dit abonnement udløber om ${daysUntilExpiry} dage`}
            </div>
            <div className="text-sm text-muted-foreground mt-0.5">
              Forny nu for at undgå afbrydelse af din adgang til videokonferencer.
            </div>
          </div>
        </div>
      )}

      {/* Recurring-notice banner */}
      <div className="mb-8 flex items-start gap-3 rounded-lg border border-volt/30 bg-volt/5 p-4 animate-fade-up" data-testid="no-recurring-notice">
        <ShieldAlert className="w-5 h-5 text-volt shrink-0 mt-0.5" />
        <div>
          <div className="font-semibold text-sm">Ingen automatisk fornyelse</div>
          <div className="text-sm text-muted-foreground mt-0.5">
            Der sker <strong>ingen tilbagevendende betaling</strong>. Du betaler kun for den valgte periode. Når abonnementet udløber, kan du selv forny det.
          </div>
        </div>
      </div>

      {/* Kampagne — only for first-time customers */}
      {isNewCustomer && (
        <div className="mb-8 flex items-start gap-3 rounded-lg border border-orange-500/40 bg-orange-500/10 p-4 animate-fade-up" data-testid="kampagne-notice">
          <div className="w-5 h-5 rounded-full bg-orange-500 flex items-center justify-center shrink-0 mt-0.5">
            <span className="text-black text-[10px] font-bold">%</span>
          </div>
          <div>
            <div className="font-semibold text-sm text-orange-400">KAMPAGNE</div>
            <div className="text-sm text-muted-foreground mt-0.5">
              Lige nu kan du tilmelde dig til en helt speciel førstegangspris.
            </div>
          </div>
        </div>
      )}

      {/* Plan cards */}
      {loading ? (
        <div className="py-16 text-center text-muted-foreground">Indlæser priser…</div>
      ) : isNewCustomer ? (
        // FIRST payment: 2 boxes — Timer (primary, first) + 1 måned
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          {/* Timer (first-time) — dynamisk tekst fra PARAMETERS, primary offer */}
          <div className="rounded-lg border-2 border-volt/40 bg-gradient-to-br from-[#18181B] to-[#0f0f10] p-8 relative overflow-hidden animate-fade-up" data-testid="plan-hourly-card">
            <div className="absolute -right-16 -top-16 w-48 h-48 rounded-full bg-volt/5 blur-3xl" />
            <div className="relative">
              <Badge className="bg-volt text-black hover:bg-volt mb-4">TIMER · FLEKSIBEL</Badge>
              <h2 className="font-display text-2xl md:text-3xl font-bold tracking-tight mb-2">Køb taletid</h2>
              <div className="flex items-baseline gap-2 mb-1">
                <span className="font-display text-5xl font-bold text-volt" data-testid="hourly-price">{fmtPrice(hourlyIntro)}</span>
                <span className="text-muted-foreground">DKK</span>
              </div>
              <p className="text-xs text-muted-foreground mb-4" data-testid="hourly-desc">
                Køb taletid i takt med dit behov. (introduktionspris) <strong>{hoursLabel}</strong>. Den købte tid skal bare bruges indenfor 12 måneder, og du betaler kun <strong>{fmtPrice(hourlyIntro)} DKK</strong>.
              </p>
              <PriceBreakdown total={hourlyIntro} testIdPrefix="hourly-intro" />
              <ul className="space-y-2 text-sm mb-8">
                {[`${hoursLabel} taletid`, 'Gyldig i op til 12 måneder', 'Ingen automatisk fornyelse', 'HD-video + ende-til-ende kryptering', 'Tal anonymt eller med video'].map((f) => (
                  <li key={f} className="flex items-center gap-2 text-muted-foreground">
                    <Check className="w-4 h-4 text-volt shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => buy('hourly')}
                disabled={buying !== null}
                size="lg"
                className="w-full bg-volt text-black hover:bg-volt hover:-translate-y-[1px] hover:shadow-xl transition-all font-semibold"
                data-testid="buy-hourly-btn"
              >
                <CreditCard className="w-4 h-4 mr-2" />
                {buying === 'hourly' ? 'Sender til betaling…' : `Køb ${hoursLabel}`}
              </Button>
            </div>
          </div>

          {/* 1 måned first-time */}
          <div className="rounded-lg border border-border bg-[#18181B] p-8 relative overflow-hidden animate-fade-up" data-testid="plan-monthly-card" style={{ animationDelay: '80ms' }}>
            <Badge className="bg-volt/10 text-volt border-volt/30 hover:bg-volt/10 mb-4">MÅNEDLIG</Badge>
            <h2 className="font-display text-2xl md:text-3xl font-bold tracking-tight mb-2">1 måneds adgang</h2>
            <div className="flex items-baseline gap-2 mb-1">
              <span className="font-display text-5xl font-bold text-volt" data-testid="monthly-price">{fmtPrice(monthlyIntro)}</span>
              <span className="text-muted-foreground">DKK</span>
            </div>
            <p className="text-xs text-muted-foreground mb-4">
              Introduktionspris første måned. Herefter <strong>{fmtPrice(monthlyRegular)} DKK/md</strong>, hvis du vælger at forny.
            </p>
            <PriceBreakdown total={monthlyIntro} testIdPrefix="monthly-intro" />
            <ul className="space-y-2 text-sm mb-8">
              {['30 dages adgang', 'HD-video + ende-til-ende kryptering', 'Tal anonymt eller med video', 'Tal så meget du vil', `Ned til DKK ${fmtPrice(monthlyIntro / 31)} pr. dag`].map((f) => (
                <li key={f} className="flex items-center gap-2 text-muted-foreground">
                  <Check className="w-4 h-4 text-volt shrink-0" /> {f}
                </li>
              ))}
            </ul>
            <Button
              onClick={() => buy('monthly')}
              disabled={buying !== null}
              size="lg"
              className="w-full bg-volt text-black hover:bg-volt hover:-translate-y-[1px] hover:shadow-xl transition-all font-semibold"
              data-testid="buy-monthly-btn"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              {buying === 'monthly' ? 'Sender til betaling…' : 'Køb månedligt'}
            </Button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          {/* Timer (recurring) — dynamisk tekst fra PARAMETERS, primary offer */}
          <div className="rounded-lg border-2 border-volt/40 bg-gradient-to-br from-[#18181B] to-[#0f0f10] p-8 relative overflow-hidden animate-fade-up" data-testid="plan-hourly-card">
            <div className="absolute -right-16 -top-16 w-48 h-48 rounded-full bg-volt/5 blur-3xl" />
            <div className="relative">
              <Badge className="bg-volt text-black hover:bg-volt mb-4">TIMER · FLEKSIBEL</Badge>
              <h2 className="font-display text-2xl md:text-3xl font-bold tracking-tight mb-2">Køb taletid</h2>
              <div className="flex items-baseline gap-2 mb-1">
                <span className="font-display text-5xl font-bold text-volt" data-testid="hourly-price">{fmtPrice(hourlyRegular)}</span>
                <span className="text-muted-foreground">DKK</span>
              </div>
              <p className="text-xs text-muted-foreground mb-4" data-testid="hourly-desc">
                Køb taletid i takt med dit behov. <strong>{hoursLabel}</strong>. Den købte tid skal bare bruges indenfor 12 måneder, og du betaler kun <strong>{fmtPrice(hourlyRegular)} DKK</strong>.
              </p>
              <PriceBreakdown total={hourlyRegular} testIdPrefix="hourly-regular" />
              <ul className="space-y-2 text-sm mb-8">
                {[`${hoursLabel} taletid`, 'Gyldig i op til 12 måneder', 'Ingen automatisk fornyelse', 'HD-video + ende-til-ende kryptering', 'Tal anonymt eller med video'].map((f) => (
                  <li key={f} className="flex items-center gap-2 text-muted-foreground">
                    <Check className="w-4 h-4 text-volt shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => buy('hourly')}
                disabled={buying !== null}
                size="lg"
                className="w-full bg-volt text-black hover:bg-volt hover:-translate-y-[1px] hover:shadow-xl transition-all font-semibold"
                data-testid="buy-hourly-btn"
              >
                <CreditCard className="w-4 h-4 mr-2" />
                {buying === 'hourly' ? 'Sender til betaling…' : `Køb ${hoursLabel}`}
              </Button>
            </div>
          </div>

          {/* Monthly (recurring) */}
          <div className="rounded-lg border border-border bg-[#18181B] p-8 relative overflow-hidden animate-fade-up" data-testid="plan-monthly-card" style={{ animationDelay: '80ms' }}>
            <Badge className="bg-volt/10 text-volt border-volt/30 hover:bg-volt/10 mb-4">MÅNEDLIG</Badge>
            <h2 className="font-display text-2xl md:text-3xl font-bold tracking-tight mb-2">1 måneds adgang</h2>
            <div className="flex items-baseline gap-2 mb-1">
              <span className="font-display text-5xl font-bold text-volt" data-testid="monthly-price">{fmtPrice(monthlyRegular)}</span>
              <span className="text-muted-foreground">DKK / md</span>
            </div>
            <p className="text-xs text-muted-foreground mb-4">Regulær månedlig pris, inkl. moms.</p>
            <PriceBreakdown total={monthlyRegular} testIdPrefix="monthly-regular" />
            <ul className="space-y-2 text-sm mb-8">
              {['30 dages adgang', 'HD-video + ende-til-ende kryptering', 'Tal anonymt eller med video', 'Tal så meget du vil', `Ned til DKK ${fmtPrice(monthlyRegular / 31)} pr. dag`].map((f) => (
                <li key={f} className="flex items-center gap-2 text-muted-foreground">
                  <Check className="w-4 h-4 text-volt shrink-0" /> {f}
                </li>
              ))}
            </ul>
            <Button
              onClick={() => buy('monthly')}
              disabled={buying !== null}
              size="lg"
              className="w-full bg-volt text-black hover:bg-volt hover:-translate-y-[1px] hover:shadow-xl transition-all font-semibold"
              data-testid="buy-monthly-btn"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              {buying === 'monthly' ? 'Sender til betaling…' : 'Køb månedligt'}
            </Button>
          </div>
        </div>
      )}

      {/* Historik — transactions from INVOICES file (only for existing customers) */}
      {!isNewCustomer && (
        <div className="animate-fade-up mb-12" style={{ animationDelay: '120ms' }}>
          <h2 className="font-display text-2xl font-bold tracking-tight mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-volt" /> Historik
          </h2>
          <div className="rounded-lg border border-border bg-[#18181B] overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead>Fakturanr.</TableHead>
                  <TableHead>Betalingsdato</TableHead>
                  <TableHead>Beløb inkl. moms</TableHead>
                  <TableHead>Plan</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody data-testid="subscription-history-table">
                {loading ? (
                  <TableRow className="border-border"><TableCell colSpan={4} className="text-center text-muted-foreground">Indlæser…</TableCell></TableRow>
                ) : invoices.length === 0 ? (
                  <TableRow className="border-border"><TableCell colSpan={4} className="text-center text-muted-foreground py-8">Ingen fakturaer endnu.</TableCell></TableRow>
                ) : (
                  invoices.map((inv) => (
                    <TableRow key={inv.id} className="border-border">
                      <TableCell className="font-mono">#{inv.invoice_number}</TableCell>
                      <TableCell>{fmtDate(inv.payment_date || inv.invoice_date)}</TableCell>
                      <TableCell>{fmtPrice(inv.amount_incl_vat)} DKK</TableCell>
                      <TableCell className="capitalize">{inv.plan || '—'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
}
